import React, { LegacyRef, useEffect, useRef, useState } from 'react';
import { PersonInformation } from '../../../../components/molecules/PersonInformation';
import { Namespace } from '../../../../translation/namespace';
import { StateIndicator } from '../../../../_lib/components/StateIndicator';
import { Pagination, PaginationItemWithFetchLink } from '../../../../_lib/components/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableModifier,
  TableRow,
} from '../../../../components/organisms/Table';
import { ISonURL } from '../../../../types/IUrl';
import { useFetch } from '../../../../hooks/useFetch';
import { sendLiveRegionMessage } from '../../../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';
import { LoadingSpinner } from '../../../../components/atoms/LoadingSpinner';
import { RefetchBlock } from '../../../../components/molecules/RefetchBlock';
import { focusNonFocusableElement } from '../../../../lib/DomUtils/utils';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { PaginationInfo } from '../../../../types/api/PaginationInfo';
import { PageFetchLinkItem } from '../../../../_lib/components/Pagination/Pagination';
import { SkipLink, SkipLinkTarget } from '../../../../_lib/components/SkipLink';
import { ExportDownloadControls, useExportDownload } from '../../../../_lib/components/ExportDownloadControls';

type Person = {
  id: string;
  profileName: string;
  orgUnit: string;
  emailLink: ISonURL;
  profileLink: ISonURL;
  confirmed: boolean;
};

type Confirmation = {
  unconfirmed: number;
};

type MandatoryAnnouncementsAnalysisProps = {
  persons: Person[];
  pageItems: PaginationItemWithFetchLink[];
  paginationInfo: PaginationInfo;
  confirmation: Confirmation;
  exportUrl?: string;
};

export function MandatoryAnnouncementsAnalysis({
  persons,
  pageItems,
  paginationInfo,
  confirmation,
  exportUrl,
}: MandatoryAnnouncementsAnalysisProps) {
  const [pageData, setPageData] = useState({
    page: pageItems[0] as PageFetchLinkItem | undefined,
    persons,
    pageItems,
    paginationInfo,
    confirmation,
  });
  const { t } = useSonTranslation('Announcement');
  const { state, objectUrl, fileName, fileNameWithoutExtension, generateExport } = useExportDownload(exportUrl, 'csv');

  const { get, requestError, isLoading } = useFetch();
  const tableContainerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (isLoading) {
      sendLiveRegionMessage(t(`${Namespace.phrases}:contentIsLoading`));
    }
  }, [t, isLoading]);

  useEffect(() => {
    // after pageData is applied, focus should be on table container, to make sure,
    // user is at the beginning of the table
    // scrolling will be set to top to not confuse visual users
    if (tableContainerRef.current) {
      focusNonFocusableElement(tableContainerRef.current);
    }

    const scrollingElement = document.getElementById('modal-dialog');

    if (scrollingElement?.scrollTo) {
      scrollingElement.scrollTo(0, 0);
    }
  }, [pageData]);

  const fetchCurrentPage = async (page: PageFetchLinkItem) => {
    const res = await get(page.fetchLink.fetchUrl);
    if (res?.data) {
      setPageData({ page, ...res.data });
    }
  };

  const getTableRows = (personsArray: Person[]) =>
    personsArray.map((person: Person) => (
      <TableRow key={person.profileLink.url}>
        <TableCell>
          <PersonInformation
            key={person.profileLink.url}
            profileName={person.profileName}
            profileLink={person.profileLink}
            orgUnit={person.orgUnit}
            emailLink={person.emailLink}
          />
        </TableCell>
        <TableCell>
          <p className="mandatory-announcements-analysis__state">
            <StateIndicator key={person.profileLink.url} status={person.confirmed ? 'green' : 'red'} />
            <span>
              {person.confirmed
                ? t('interactions.analysis.table.confirmed')
                : t('interactions.analysis.table.notConfirmed')}
            </span>
          </p>
        </TableCell>
      </TableRow>
    ));

  const getTableContent = (personsArray: Person[], currentPage: number, totalPages: number) => {
    if (isLoading) {
      // initial loading spinner
      return (
        <div className="mandatory-announcements-analysis__spinner">
          <div className="mandatory-announcements-analysis__spinner-container">
            <LoadingSpinner />
          </div>
        </div>
      );
    }
    if (requestError) {
      return (
        <RefetchBlock
          // As above we must pass here also for refetchBlock function set focus
          refetchBlock={() => pageData.page && fetchCurrentPage(pageData.page)}
        />
      );
    }
    return (
      <div
        className="mandatory-announcements-analysis__table-container"
        id="mandatory-announcements-analysis-table-container"
        ref={tableContainerRef as unknown as LegacyRef<HTMLDivElement> | undefined}
      >
        <div className="mandatory-announcements-analysis__skip-link">
          <SkipLink id="mandatory-announcements-analysis-pagination" focusTarget>
            {t<string>('interactions.analysis.skipLink')}
          </SkipLink>
        </div>
        <Table
          caption={t('interactions.analysis.table.caption', { currentPage, totalPages })}
          key={`page-${currentPage}`}
          captionHidden
        >
          <TableHead className="tables__head--invisible">
            <TableRow>
              <TableCell>{t('interactions.analysis.table.person')}</TableCell>
              <TableCell modifier={TableModifier.WIDTH_140}>{t('interactions.analysis.table.state')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{getTableRows(personsArray)}</TableBody>
        </Table>
      </div>
    );
  };

  const getPageContainer = (pageInfoObject: PaginationInfo, personsArray: Person[]) => {
    const pageContent = [];
    const { currentPage, totalPages } = pageInfoObject;

    for (let index = 1; index <= totalPages; index += 1) {
      pageContent.push(
        <div
          key={`page-${index}`}
          id={`page-${index}`}
          className={
            index === currentPage
              ? 'mandatory-announcements-analysis__page mandatory-announcements-analysis__page--visible'
              : 'mandatory-announcements-analysis__page'
          }
          data-current={index === currentPage ? true : null}
        >
          {index === currentPage ? getTableContent(personsArray, index, paginationInfo.totalPages) : null}
        </div>
      );
    }
    return pageContent;
  };

  return (
    <div className="mandatory-announcements-analysis">
      <p className="mandatory-announcements-analysis__summary">
        {pageData.confirmation.unconfirmed !== 1
          ? t('interactions.analysis.summary_plural', { unconfirmed: pageData.confirmation.unconfirmed })
          : t('interactions.analysis.summary', { unconfirmed: pageData.confirmation.unconfirmed })}
      </p>
      <div className="mandatory-announcements-analysis__scroll-container">
        {getPageContainer(pageData.paginationInfo, pageData.persons)}
        <div className="mandatory-announcements-analysis__pagination">
          <SkipLinkTarget
            targetName={t('interactions.analysis.skipLinkTarget')}
            id="mandatory-announcements-analysis-pagination"
          />
          <Pagination pageItems={pageData.pageItems} onClick={fetchCurrentPage} />
          {exportUrl && (
            <ExportDownloadControls
              buttonLabel={t('interactions.analysis.exportCsv.title')}
              fileExtension="csv"
              onGenerate={generateExport}
              fetchState={state}
              objectUrl={objectUrl}
              fileName={fileNameWithoutExtension}
              download={fileName}
            />
          )}
        </div>
      </div>
    </div>
  );
}
